/**
 * 공통 클라이언트 사이드 테이블 정렬 훅.
 *
 * 사내 표 어디서나 쓰는 패턴(컬럼 헤더 클릭 → asc/desc 토글, 동일 키 두 번 클릭이면 방향 반전)
 * 을 한 곳에 묶어 다른 탭과 일관된 UX 를 보장한다.
 *
 * @example
 *   const { toggleSort, sortLabel, sortRows } = useTableSort("transactionDate", "desc");
 *   const sortedRows = useMemo(() => sortRows(rows, {
 *       amount: r => r.type === "INCOME" ? r.amount : -r.amount,   // 수입은 +, 지출은 - 로 정렬
 *   }), [rows, sortRows]);
 *
 *   <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("transactionDate")}>
 *     {sortLabel("transactionDate", "일자")}
 *   </TableCell>
 *
 * 정렬 키와 다르게 비교해야 하는 컬럼은 두 번째 인자 `accessors` 로 함수를 줄 수 있다.
 */
import { useCallback, useState } from "react";

export function useTableSort(initialKey = null, initialDir = "desc") {
    const [sort, setSort] = useState({ key: initialKey, dir: initialDir });

    const toggleSort = useCallback((key) =>
        setSort(s => ({
            key,
            dir: s.key === key && s.dir === "asc" ? "desc" : "asc",
        })), []);

    const sortLabel = useCallback((key, label) =>
        `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`,
    [sort]);

    /** rows 를 현재 sort 상태대로 정렬한 새 배열 반환. accessors 는 키별 비교값 추출 함수. */
    const sortRows = useCallback((rows, accessors = {}) => {
        if (!sort.key || !Array.isArray(rows)) return rows;
        const get = accessors[sort.key] || ((r) => r?.[sort.key]);
        const factor = sort.dir === "asc" ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = get(a);
            const bv = get(b);
            // null/undefined 는 항상 뒤로
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * factor;
            return String(av).localeCompare(String(bv), "ko") * factor;
        });
    }, [sort]);

    return { sort, setSort, toggleSort, sortLabel, sortRows };
}
